import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ButtonInfo {
	id: string;
	name: string;
	type: string;
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface PopupState {
	isGroupVisible: boolean;
	isCapacityVisible: boolean;
	groupX: number;
	groupY: number;
	capacityX: number;
	capacityY: number;
	activeButtonId: string | null;
	activeButtonType: string | null;
	capacityButton: ButtonInfo | null;
}

export interface TooltipState {
	isVisible: boolean;
	x: number;
	y: number;
	position: string;
	fullSizeName: string;
}

@Injectable({
	providedIn: 'root'
})
export class ButtonInteractionService {
	private buttons: ButtonInfo[] = [];
	private gridSize: number = 35;

	// États des popups
	private popupState: PopupState = {
		isGroupVisible: false,
		isCapacityVisible: false,
		groupX: 0,
		groupY: 0,
		capacityX: 0,
		capacityY: 0,
		activeButtonId: null,
		activeButtonType: null,
		capacityButton: null
	};

	// État du tooltip
	private tooltipState: TooltipState = {
		isVisible: false,
		x: 0,
		y: 0,
		position: 'bottom',
		fullSizeName: ''
	};

	// Observables
	private popupSubject = new BehaviorSubject<PopupState>(this.popupState);
	private tooltipSubject = new BehaviorSubject<TooltipState>(this.tooltipState);

	public popup$ = this.popupSubject.asObservable();
	public tooltip$ = this.tooltipSubject.asObservable();

	setGridSize(gridSize: number): void {
		this.gridSize = gridSize;
	}

	registerButton(id: string, name: string, type: string, x: number, y: number, width: number, height: number): void {
		this.buttons.push({ id, name, type, x, y, width, height });
	}

	clearButtons(): void {
		this.buttons = [];
	}

	getButtons(): ButtonInfo[] {
		return [...this.buttons];
	}

	findButtonAtPosition(mouseX: number, mouseY: number): ButtonInfo | null {
		return this.buttons.find(button =>
			mouseX >= button.x &&
			mouseX <= button.x + button.width &&
			mouseY >= button.y &&
			mouseY <= button.y + button.height
		) || null;
	}

	getCursorStyle(hoveredButton: ButtonInfo | null, mouseX: number, mouseY: number, isDragging: boolean): string {
		if (isDragging) {
			return 'grabbing';
		}

		if (!hoveredButton) {
			return 'grab';
		}

		if (hoveredButton.type === 'child-capacity') {
			const isInSpecialZone =
				mouseX >= hoveredButton.x + this.gridSize &&
				mouseX <= hoveredButton.x + (this.gridSize * 2) &&
				mouseY >= hoveredButton.y + this.gridSize &&
				mouseY <= hoveredButton.y + (this.gridSize * 2);

			return isInSpecialZone ? 'pointer' : 'grab';
		}

		if (hoveredButton.type === 'tool-tip-name') {
			return 'grab';
		}

		return 'pointer';
	}

	handleButtonClick(
		buttonId: string,
		buttonType: string,
		button: ButtonInfo,
		isPlus: boolean,
		canvasRect: DOMRect,
		scale: number
	): { action: string; data?: any } {

		this.popupState.activeButtonId = buttonId;
		this.popupState.activeButtonType = buttonType;

		const buttonCanvasX = button.x;
		const buttonCanvasY = button.y;

		const buttonScreenX = canvasRect.left + (buttonCanvasX * scale);
		const buttonScreenY = canvasRect.top + (buttonCanvasY * scale);
		const buttonScreenWidth = button.width * scale;
		const buttonScreenHeight = button.height * scale;

		switch (buttonType) {
			case 'group-option':
				this.popupState.groupX = buttonScreenX + (buttonScreenWidth / 2) - 124;
				this.popupState.groupY = buttonScreenY + buttonScreenHeight;
				this.popupState.isGroupVisible = true;
				this.popupState.isCapacityVisible = false;
				this.popupState.capacityButton = button;
				this.emitPopupState();
				return { action: 'showGroupPopup' };

			case 'capacity-option':
				this.popupState.capacityX = buttonScreenX + (buttonScreenWidth / 2) - 62;
				this.popupState.capacityY = buttonScreenY + buttonScreenHeight;
				this.popupState.isCapacityVisible = true;
				this.popupState.isGroupVisible = false;
				this.popupState.capacityButton = button;
				this.emitPopupState();
				return { action: 'showCapacityPopup' };

			case 'app-button':
				return { action: 'openApplicationDrawer', data: { applicationId: button.id } };

			case 'child-capacity':
				if (isPlus) {
					return { action: 'openPlusDialog', data: { buttonId, button } };
				}
				break;

			case 'add-group':
				return { action: 'addGroup' };

			case 'add-capacity':
				this.popupState.capacityButton = button;
				return { action: 'addCapacity', data: { button } };

			default:
				return { action: 'none' };
		}

		return { action: 'none' };
	}

	updateTooltip(
		button: ButtonInfo | null,
		mouseEvent: MouseEvent,
		canvasRect: DOMRect,
		scale: number,
		translateFn: (key: string) => string
	): void {
		if (!button) {
			this.tooltipState.isVisible = false;
			this.emitTooltipState();
			return;
		}

		const mouseX = (mouseEvent.clientX - canvasRect.left) / scale;
		const mouseY = (mouseEvent.clientY - canvasRect.top) / scale;

		if (button.type === "child-capacity") {
			if (
				mouseX >= button.x + this.gridSize &&
				mouseX <= button.x + (this.gridSize * 2) &&
				mouseY >= button.y + this.gridSize &&
				mouseY <= button.y + (this.gridSize * 2)
			) {
				const buttonScreenX = canvasRect.left + ((button.x + this.gridSize) * scale);
				const buttonScreenY = canvasRect.top + ((button.y + this.gridSize) * scale);
				const buttonScreenWidth = this.gridSize * scale;
				const buttonScreenHeight = this.gridSize * scale;

				this.tooltipState.x = buttonScreenX + (buttonScreenWidth / 2);
				this.tooltipState.y = buttonScreenY + buttonScreenHeight + 10;
				this.tooltipState.fullSizeName = translateFn('page.diagram.capacity.capacity-menu.add-app');
				this.tooltipState.isVisible = true;
				this.emitTooltipState();
			} else {
				this.tooltipState.isVisible = false;
				this.emitTooltipState();
			}
		}

		if (button.type === "tool-tip-name" || button.type === "add-group" || button.type === "add") {
			const buttonScreenX = canvasRect.left + (button.x * scale);
			const buttonScreenY = canvasRect.top + (button.y * scale);
			const buttonScreenWidth = button.width * scale;
			const buttonScreenHeight = button.height * scale;

			this.tooltipState.x = buttonScreenX + (buttonScreenWidth / 2);
			this.tooltipState.y = buttonScreenY + buttonScreenHeight + 10;

			if (button.type === "add-group") {
				this.tooltipState.fullSizeName = translateFn('page.diagram.capacity.create-group');
			} else {
				this.tooltipState.fullSizeName = button.name;
			}

			this.tooltipState.isVisible = true;
			this.emitTooltipState();
		}
	}

	closePopups(): void {
		this.popupState.isGroupVisible = false;
		this.popupState.isCapacityVisible = false;
		this.popupState.activeButtonId = null;
		this.popupState.activeButtonType = null;
		this.popupState.capacityButton = null;

		this.tooltipState.isVisible = false;

		this.emitPopupState();
		this.emitTooltipState();

		// Masquer les éléments DOM
		const popupGroup = document.querySelector('.popup-group') as HTMLElement;
		const popupCapacity = document.querySelector('.popup') as HTMLElement;

		if (popupGroup) popupGroup.style.display = 'none';
		if (popupCapacity) popupCapacity.style.display = 'none';
	}

	updatePopupPosition(selector: string, x: number, y: number, containerElement: HTMLElement): void {
		setTimeout(() => {
			const popupElement = document.querySelector(selector) as HTMLElement;
			if (popupElement) {
				popupElement.style.display = 'block';
				popupElement.style.position = 'fixed';
				popupElement.style.left = `${x}px`;
				popupElement.style.top = `${y}px`;
				popupElement.style.transform = 'translateX(-50%)';

				this.adjustPopupPosition(popupElement, containerElement);
			}
		}, 0);
	}

	private adjustPopupPosition(popupElement: HTMLElement, containerElement: HTMLElement): void {
		const rect = popupElement.getBoundingClientRect();
		const viewportWidth = containerElement.clientWidth;
		const viewportHeight = containerElement.clientHeight;

		if (rect.right > viewportWidth) {
			// Ajustement si nécessaire
		} else if (rect.left < 120) {
			popupElement.style.left = `120px`;
			popupElement.style.transform = 'none';
		}

		if (rect.bottom > viewportHeight + 190) {
			popupElement.style.top = `${rect.top - rect.height - 35}px`;
		}
	}

	getPopupState(): PopupState {
		return { ...this.popupState };
	}

	getTooltipState(): TooltipState {
		return { ...this.tooltipState };
	}

	isSpecialZoneClick(button: ButtonInfo, clickX: number, clickY: number): boolean {
		return button.type === 'child-capacity' &&
			clickX >= button.x + this.gridSize &&
			clickX <= button.x + (this.gridSize * 2) &&
			clickY >= button.y + this.gridSize &&
			clickY <= button.y + (this.gridSize * 2);
	}

	private emitPopupState(): void {
		this.popupSubject.next({ ...this.popupState });
	}

	private emitTooltipState(): void {
		this.tooltipSubject.next({ ...this.tooltipState });
	}

	reset(): void {
		this.clearButtons();
		this.closePopups();
	}
}
